/**
 * cache/walls.ts — THE canonical cache wall geometry, shared by both sides.
 *
 * ── WHY THIS FILE EXISTS ──────────────────────────────────────────────────────
 * Cache walls used to be a CLIENT-ONLY fact. The dressing layer emitted them
 * (`client/src/cache/theme/common.ts`), folded them into a {@link BoxColliderField}
 * and hung that field on the planet's homestead hooks — so a player could not WALK
 * through a wall, and nothing else in the game knew the wall was there. Measured
 * consequences, all reported from live play:
 *
 *   1. Bolts flew straight through masonry in BOTH directions. The server's bolt sim
 *      consults terrain and `settlementCrossingLocal` (settlement seeds grown from a
 *      planet's city graph) — and a pad world has no settlements, so the consult
 *      returned null and every bolt sailed on. The client's predicted bolt tested
 *      `planet.settlementColliders`, empty for the same reason.
 *   2. Drones acquired, shot and pathed through walls: `ai/perception.ts` had no
 *      occlusion term at all, and the drone→player damage path is a scheduled hit
 *      with no geometry test whatsoever.
 *
 * The fix is this module: ONE pure, pad-local description of a cache's solids that
 * client and server fold identically. The client keeps its themes (a vault's ceilings
 * and a hollow's rock prisms are DRESSING and stay client-side); what moves here is
 * the part both sides must agree on — room-boundary walls, corridor flanks and gate
 * blockers.
 *
 * ── SPACE ────────────────────────────────────────────────────────────────────
 * Pad-local (x, z) metres about the pad world's +Y pole, heights measured ABOVE the
 * pad datum sphere — the same frame `CacheLayoutV1` is written in. {@link wallSpec}
 * is the ONE conversion to the planet-local {@link BoxSpec} the collider field
 * ingests: base at `y = √((R+h)² − x² − z²)`.
 *
 * ── PMATH ────────────────────────────────────────────────────────────────────
 * Axis-aligned pad math only: no trig. `Math.sqrt` is not a banned intrinsic (it is
 * IEEE-754 exact); the two vector normalisations that want a 3-D length use
 * `pmath.hypot`.
 */
import * as pmath from "../pmath.js";
import { BoxColliderField, COLLIDER_MAX_M, type BoxSpec } from "../worldgen/boxColliders.js";
import type { CacheLayoutV1, CacheTheme } from "./types.js";

// ── the box vocabulary ────────────────────────────────────────────────────────

/**
 * One solid box in pad-local terms — the single description both the collider
 * ({@link BoxSpec}) and the client's mesh derive from, so what you see is what you
 * hit. `along` is the horizontal axis the box is LONG on; thickness runs on the other.
 */
export interface CacheWallBox {
  /** Pad-local centre of the box's base footprint. */
  cx: number;
  cz: number;
  along: "x" | "z";
  /** Half the length along `along`. */
  halfLen: number;
  /** Half the thickness across `along`. */
  halfT: number;
  /** Absolute floor height (above the base sphere) the box stands on. */
  baseH: number;
  /** Full height above `baseH`. */
  height: number;
}

/** A gate barrier and whether it currently stands. `edgeIndex` indexes `layout.edges`. */
export interface CacheGateBox {
  edgeIndex: number;
  box: CacheWallBox;
  open: boolean;
}

/** The canonical solids of one layout — {@link cacheWallBoxes}' whole output. */
export interface CacheWallSet {
  /** Static vertical solids, in build order. */
  walls: CacheWallBox[];
  /**
   * Overhead LIDS — one per room and one per corridor chunk, at the theme's canonical
   * enclosure height. EMPTY for open-sky themes (`ruin`), which is a design statement,
   * not an omission: a ruin is outdoors, and a bolt arcing over a broken wall into the
   * next courtyard is legitimate. See {@link CacheWallParams.ceilH}.
   */
  ceilings: CacheWallBox[];
  /** One barrier per `gate` edge, all CLOSED (the server owns opening them). */
  gates: CacheGateBox[];
}

/**
 * Extra depth a solid box sinks below its floor when a cache is played LIVE.
 *
 * The live host is `cachePadSystem`'s planet. MEASURED across 60 instances its
 * sampler varies by a median 1.44 m (p90 2.39, max 3.24) over a ±100 m layout
 * footprint, and the dressing's floors sit on the datum — so a player can stand that
 * far BELOW a wall's base, and `BoxColliderField.resolve` rejects a query outside a
 * box's radial span. 2.5 m clears the relief twice over and costs nothing: the box's
 * TOP is unchanged (height grows with the sink).
 */
export const LIVE_SINK_EXTRA_M = 2.5;

// ── the walkable plan ─────────────────────────────────────────────────────────

/** Sides of a room rectangle: 0 = +x, 1 = −x, 2 = +z, 3 = −z. */
export const SIDE_DX = [1, -1, 0, 0];
export const SIDE_DZ = [0, 0, 1, -1];

export function sideOf(dx: number, dz: number): number {
  if (dx > 0) return 0;
  if (dx < 0) return 1;
  return dz > 0 ? 2 : 3;
}

interface RoomFeat { x0: number; x1: number; z0: number; z1: number; h: number }

export interface CorrFeat {
  /** Flat [x, z, x, z, …] polyline: room-A mouth → cell centres → room-B mouth. */
  pts: number[];
  /** Cumulative arc length at each point. */
  cum: number[];
  len: number;
  /** Floor height at each polyline NODE (interior nodes carry the SHARED cell height). */
  hs: number[];
}

/** Height-blend falloff: a feature `d` metres away votes with 1/(d + SOFT)⁴. */
const BLEND_SOFT_M = 0.6;
/** Clamp on that denominator — a feature you are well inside wins outright. */
const BLEND_MIN_M = 0.05;

/** Scratch for the nearest-point query (hot path — no allocation). */
const _near = { d: 0, seg: 0, s: 0 };

function nearestOnPoly(c: CorrFeat, x: number, z: number): void {
  let bd = Infinity, bseg = 0, bs = 0;
  for (let i = 0; i + 3 < c.pts.length; i += 2) {
    const ax = c.pts[i], az = c.pts[i + 1], bx = c.pts[i + 2], bz = c.pts[i + 3];
    const ex = bx - ax, ez = bz - az;
    const ll = ex * ex + ez * ez;
    let s = ll > 0 ? ((x - ax) * ex + (z - az) * ez) / ll : 0;
    s = s < 0 ? 0 : s > 1 ? 1 : s;
    const px = ax + ex * s, pz = az + ez * s;
    const dx = x - px, dz = z - pz;
    const d = Math.sqrt(dx * dx + dz * dz);
    if (d < bd) { bd = d; bseg = i >> 1; bs = s; }
  }
  _near.d = bd;
  _near.seg = bseg;
  _near.s = bs;
}

function corrH(c: CorrFeat): number {
  const a = c.hs[_near.seg], b = c.hs[_near.seg + 1];
  return a + (b - a) * _near.s;
}

function sdRect(r: RoomFeat, x: number, z: number): number {
  const dx = Math.max(r.x0 - x, x - r.x1);
  const dz = Math.max(r.z0 - z, z - r.z1);
  if (dx <= 0 && dz <= 0) return Math.max(dx, dz);
  const ox = Math.max(dx, 0), oz = Math.max(dz, 0);
  return Math.sqrt(ox * ox + oz * oz);
}

/** One nearest-feature answer: signed distance (negative inside) + floor height. */
export interface WalkSample { d: number; h: number }

const bucketKey = (i: number, j: number): number => (i + 4096) * 16384 + (j + 4096);

/**
 * The walkable plan of a layout: room rectangles + corridor polylines, bucketed by
 * working cell, answering "how far is walkable ground and how high is its floor".
 *
 * ⚠ MOVED HERE FROM `client/src/cache/theme/common.ts` UNCHANGED. It was always pure
 * `f(layout, floorH)` — the only reason it lived client-side is that the dressing was
 * the only consumer. The server now needs the same plan to know where the walls are,
 * and two copies of this would be two dungeons. The client re-exports it as
 * `WalkField`, so every theme keeps its byte-identical geometry.
 *
 * Two corridors are allowed to CROSS (space.ts only forbids routing through a third
 * ROOM), and when they do they must agree about the ground at the shared cell —
 * otherwise the nearest-feature height field puts a multi-metre step across the
 * crossing, which reads as a cliff in the middle of a corridor. Every corridor through
 * a cell votes with the height its own ramp wants there; the cell takes the mean.
 */
export class CacheWalkField {
  readonly rooms: RoomFeat[];
  readonly corrs: CorrFeat[];
  /** Per-room bitmask of the sides carrying a corridor mouth (see {@link sideOf}). */
  readonly mouths: number[];
  readonly corridorHalf: number;

  private readonly bucket = new Map<number, number[]>();
  private readonly cellM: number;
  private readonly _s: WalkSample = { d: 0, h: 0 };

  constructor(layout: CacheLayoutV1, floorH: ArrayLike<number>, corridorHalf: number, pad: number) {
    this.corridorHalf = corridorHalf;
    this.cellM = layout.cell;
    const rooms = layout.rooms;
    this.rooms = rooms.map((r, i) => ({
      x0: r.x - r.w / 2, x1: r.x + r.w / 2,
      z0: r.z - r.d / 2, z1: r.z + r.d / 2,
      h: floorH[i],
    }));
    this.mouths = rooms.map(() => 0);

    const half = (layout.grid - 1) / 2;
    const cellPos = (c: number): number => (c - half) * layout.cell;

    const geoms = layout.edges.map((e) => {
      const A = rooms[e.a], B = rooms[e.b];
      const cells = e.cells;
      const firstU = cells.length > 0 ? cells[0][0] : B.cu;
      const firstV = cells.length > 0 ? cells[0][1] : B.cv;
      const lastU = cells.length > 0 ? cells[cells.length - 1][0] : A.cu;
      const lastV = cells.length > 0 ? cells[cells.length - 1][1] : A.cv;
      const sa = sideOf(Math.sign(firstU - A.cu), Math.sign(firstV - A.cv));
      const sb = sideOf(Math.sign(lastU - B.cu), Math.sign(lastV - B.cv));
      this.mouths[e.a] |= 1 << sa;
      this.mouths[e.b] |= 1 << sb;
      const halfA = SIDE_DX[sa] !== 0 ? A.w / 2 : A.d / 2;
      const halfB = SIDE_DX[sb] !== 0 ? B.w / 2 : B.d / 2;
      const pts: number[] = [A.x + SIDE_DX[sa] * halfA, A.z + SIDE_DZ[sa] * halfA];
      for (const [cu, cv] of cells) pts.push(cellPos(cu), cellPos(cv));
      pts.push(B.x + SIDE_DX[sb] * halfB, B.z + SIDE_DZ[sb] * halfB);
      const cum: number[] = [0];
      for (let i = 0; i + 3 < pts.length; i += 2) {
        const dx = pts[i + 2] - pts[i], dz = pts[i + 3] - pts[i + 1];
        cum.push(cum[cum.length - 1] + Math.sqrt(dx * dx + dz * dz));
      }
      return { pts, cum, len: cum[cum.length - 1], cells, hA: floorH[e.a], hB: floorH[e.b] };
    });

    const cellVote = new Map<number, { sum: number; n: number }>();
    const cellKey = (cu: number, cv: number): number => cu * 4096 + cv;
    for (const g of geoms) {
      for (let j = 0; j < g.cells.length; j++) {
        const want = g.hA + (g.hB - g.hA) * (g.len > 0 ? g.cum[j + 1] / g.len : 0);
        const k = cellKey(g.cells[j][0], g.cells[j][1]);
        const v = cellVote.get(k);
        if (v === undefined) cellVote.set(k, { sum: want, n: 1 });
        else { v.sum += want; v.n += 1; }
      }
    }
    this.corrs = geoms.map((g) => {
      const hs: number[] = [g.hA];
      for (const [cu, cv] of g.cells) {
        const v = cellVote.get(cellKey(cu, cv))!;
        hs.push(v.sum / v.n);
      }
      hs.push(g.hB);
      return { pts: g.pts, cum: g.cum, len: g.len, hs };
    });

    // Bucket index. A query only has to see features within `pad`; past that the
    // caller's own fallback answers, with no discontinuity.
    const stamp = (id: number, x0: number, x1: number, z0: number, z1: number): void => {
      const i0 = Math.floor((x0 - pad) / this.cellM), i1 = Math.floor((x1 + pad) / this.cellM);
      const j0 = Math.floor((z0 - pad) / this.cellM), j1 = Math.floor((z1 + pad) / this.cellM);
      for (let i = i0; i <= i1; i++) {
        for (let j = j0; j <= j1; j++) {
          const k = bucketKey(i, j);
          let list = this.bucket.get(k);
          if (list === undefined) this.bucket.set(k, (list = []));
          list.push(id);
        }
      }
    };
    this.rooms.forEach((r, i) => stamp(i, r.x0, r.x1, r.z0, r.z1));
    this.corrs.forEach((c, i) => {
      let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
      for (let p = 0; p < c.pts.length; p += 2) {
        x0 = Math.min(x0, c.pts[p]); x1 = Math.max(x1, c.pts[p]);
        z0 = Math.min(z0, c.pts[p + 1]); z1 = Math.max(z1, c.pts[p + 1]);
      }
      stamp(this.rooms.length + i, x0 - corridorHalf, x1 + corridorHalf, z0 - corridorHalf, z1 + corridorHalf);
    });
  }

  /**
   * Nearest walkable feature at (x, z): `d` is its signed distance (negative inside),
   * `h` a DEPTH-WEIGHTED floor height over every feature in range.
   *
   * ⚠ THE HEIGHT IS A BLEND, NOT THE NEAREST FEATURE'S FLOOR, and that is the whole
   * point. Taking the nearest puts a STEP on the medial axis between two features at
   * different heights — the two are exactly tied there, so an epsilon of movement
   * swaps which one answers. It is invisible in an open-air theme (the ground has
   * already climbed away from both floors by then) but the cache render gate measures
   * the visual mesh against `surfaceRadiusWorld`, whose projection lands micrometres
   * to one side, and the seam showed up as a 171 mm feet-vs-visual disagreement.
   * So every feature votes with `1 / (d + SOFT)⁴`, clamped so a feature you are deep
   * inside dominates absolutely: continuous everywhere, and equal to the containing
   * feature's own floor wherever you actually stand.
   *
   * Returns a shared scratch object — read it before the next call.
   */
  sample(x: number, z: number): WalkSample {
    const out = this._s;
    const list = this.bucket.get(bucketKey(Math.floor(x / this.cellM), Math.floor(z / this.cellM)));
    if (list === undefined) { out.d = Infinity; out.h = 0; return out; }
    let bd = Infinity, wsum = 0, hsum = 0;
    for (const id of list) {
      let d: number, h: number;
      if (id < this.rooms.length) {
        const r = this.rooms[id];
        d = sdRect(r, x, z);
        h = r.h;
      } else {
        const c = this.corrs[id - this.rooms.length];
        nearestOnPoly(c, x, z);
        d = _near.d - this.corridorHalf;
        h = corrH(c);
      }
      if (d < bd) bd = d;
      const q = d + BLEND_SOFT_M > BLEND_MIN_M ? d + BLEND_SOFT_M : BLEND_MIN_M;
      const q2 = q * q;
      const w = 1 / (q2 * q2);
      wsum += w;
      hsum += w * h;
    }
    out.d = bd;
    out.h = wsum > 0 ? hsum / wsum : 0;
    return out;
  }

  /** Signed distance to the nearest walkable feature (negative inside). */
  nearestSd(x: number, z: number): number {
    return this.sample(x, z).d;
  }

  /** Floor height of the nearest walkable feature (its own floor when outside). */
  floorAt(x: number, z: number): number {
    return this.sample(x, z).h;
  }

  /** Floor height of corridor `ci` at its nearest point to (x, z). */
  corrFloorAt(ci: number, x: number, z: number): number {
    const c = this.corrs[ci];
    nearestOnPoly(c, x, z);
    return corrH(c);
  }

  /** Distance from (x, z) to corridor `ci`'s centreline. */
  corrDist(ci: number, x: number, z: number): number {
    nearestOnPoly(this.corrs[ci], x, z);
    return _near.d;
  }

  /** Highest floor of any feature the axis-aligned rect touches (ceiling sizing). */
  maxFloorOver(x0: number, x1: number, z0: number, z1: number, steps = 3): number {
    let best = -Infinity;
    for (let i = 0; i <= steps; i++) {
      for (let j = 0; j <= steps; j++) {
        const x = x0 + ((x1 - x0) * i) / steps;
        const z = z0 + ((z1 - z0) * j) / steps;
        best = Math.max(best, this.sample(x, z).h);
      }
    }
    return best;
  }
}

// ── wall emission ─────────────────────────────────────────────────────────────

export interface WallRunOptions {
  /** Wall thickness, metres. */
  wallT: number;
  /** Full height above the (sunk) base. */
  height: number;
  /** How far the box sinks below its floor so a ramp never opens a gap under it. */
  sink: number;
  /** False where the run must be CUT (a chunk centre standing on open ground). */
  allow(x: number, z: number): boolean;
  /** Cut resolution, metres. */
  chunk?: number;
}

/**
 * Emit a wall run into `walls`, CUT where it would cross open ground.
 *
 * A wall is a line in the plan, but the plan overlaps: corridors may cross each other,
 * so a corridor's side wall can run straight across another corridor's floor and seal
 * a legal route. The run is walked in short chunks, any chunk whose centre lands on
 * walkable ground is dropped, and surviving chunks are merged back into as few boxes
 * as possible.
 */
export function emitWallRun(
  walls: CacheWallBox[],
  x0: number, z0: number, x1: number, z1: number,
  along: "x" | "z",
  hAt: (x: number, z: number) => number,
  o: WallRunOptions,
): void {
  const dx = x1 - x0, dz = z1 - z0;
  const len = pmath.hypot(dx, dz);
  if (len < 0.05) return;
  const chunk = o.chunk ?? 1.0;
  const n = Math.max(1, Math.ceil(len / chunk));
  const flush = (a: number, b: number): void => {
    const t = (a + b) / 2;
    const cx = x0 + dx * t, cz = z0 + dz * t;
    const l = len * (b - a);
    if (l < 0.05) return;
    walls.push({
      cx, cz, along,
      halfLen: l / 2, halfT: o.wallT / 2,
      baseH: hAt(cx, cz) - o.sink,
      height: o.height + o.sink,
    });
  };
  let run = -1;
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) / n;
    if (o.allow(x0 + dx * t, z0 + dz * t)) { if (run < 0) run = i; }
    else if (run >= 0) { flush(run / n, i / n); run = -1; }
  }
  if (run >= 0) flush(run / n, 1);
}

/**
 * Room-perimeter walls with a gap at every corridor mouth.
 *
 * A mouth is always centred on the room's own centre line (the first corridor cell is
 * axis-aligned with the room cell), so a side carries at most one distinct opening
 * however many edges leave through it.
 */
export function emitRoomPerimeter(
  walls: CacheWallBox[],
  layout: CacheLayoutV1,
  field: CacheWalkField,
  floorH: ArrayLike<number>,
  o: WallRunOptions & {
    corridorHalf: number;
    /** Per-room wall height (a vault's walls rise to that room's own ceiling). */
    heightOf?: (roomIndex: number) => number;
  },
): void {
  layout.rooms.forEach((r, i) => {
    const h = floorH[i];
    const ro: WallRunOptions = o.heightOf ? { ...o, height: o.heightOf(i) } : o;
    const hx = r.w / 2 + o.wallT / 2, hz = r.d / 2 + o.wallT / 2;
    for (let side = 0; side < 4; side++) {
      const open = (field.mouths[i] & (1 << side)) !== 0;
      const alongX = SIDE_DZ[side] !== 0;          // ±z sides run along x
      const spanHalf = (alongX ? r.w : r.d) / 2 + o.wallT;
      const fixed = alongX ? r.z + SIDE_DZ[side] * hz : r.x + SIDE_DX[side] * hx;
      const centre = alongX ? r.x : r.z;
      const put = (lo: number, hi: number): void => {
        if (alongX) emitWallRun(walls, lo, fixed, hi, fixed, "x", () => h, ro);
        else emitWallRun(walls, fixed, lo, fixed, hi, "z", () => h, ro);
      };
      if (!open) { put(centre - spanHalf, centre + spanHalf); continue; }
      put(centre - spanHalf, centre - o.corridorHalf);
      put(centre + o.corridorHalf, centre + spanHalf);
    }
  });
}

/**
 * Corridor flank walls. At a bend only the OUTER wall may run past the corner —
 * extending the inner one would reach across the turn and seal the corridor shut.
 */
export function emitCorridorFlanks(
  walls: CacheWallBox[],
  field: CacheWalkField,
  o: WallRunOptions & { corridorHalf: number },
): void {
  const OUT = o.corridorHalf + o.wallT;
  field.corrs.forEach((c, ci) => {
    const segs = (c.pts.length - 2) / 2;
    const dirOf = (k: number): [number, number] => {
      const dx = c.pts[2 * k + 2] - c.pts[2 * k], dz = c.pts[2 * k + 3] - c.pts[2 * k + 1];
      const l = Math.sqrt(dx * dx + dz * dz) || 1;
      return [dx / l, dz / l];
    };
    for (let k = 0; k < segs; k++) {
      const [dx, dz] = dirOf(k);
      const ax = c.pts[2 * k], az = c.pts[2 * k + 1];
      const bx = c.pts[2 * k + 2], bz = c.pts[2 * k + 3];
      if (pmath.hypot(bx - ax, bz - az) < 1e-6) continue;
      for (const sgn of [1, -1]) {
        const nx = -dz * sgn, nz = dx * sgn;
        let ext0 = o.wallT, ext1 = o.wallT;
        if (k > 0) {
          const [px, pz] = dirOf(k - 1);
          ext0 = (px - dx) * nx + (pz - dz) * nz > 0 ? OUT : 0;
        }
        if (k + 1 < segs) {
          const [qx, qz] = dirOf(k + 1);
          ext1 = (dx - qx) * nx + (dz - qz) * nz > 0 ? OUT : 0;
        }
        const off = o.corridorHalf + o.wallT / 2;
        const sx = ax - dx * ext0 + nx * off, sz = az - dz * ext0 + nz * off;
        const ex = bx + dx * ext1 + nx * off, ez = bz + dz * ext1 + nz * off;
        emitWallRun(
          walls, sx, sz, ex, ez,
          Math.abs(dx) > Math.abs(dz) ? "x" : "z",
          (qx, qz) => field.corrFloorAt(ci, qx, qz),
          o,
        );
      }
    }
  });
}

/**
 * ⚠⚠ CORNER POSTS WERE TRIED AND REJECTED — 2026-07-31, keep the measurement.
 *
 * Live play reported bodies slipping OUT THROUGH cache walls "especially at corners",
 * and the obvious reading is a seam: runs that meet flush at a junction, with a hole
 * between them a wedged body squeezes through. It is not what is happening. A
 * body-radius FLOOD FILL of a tier-2 layout at 0.25 m resolution — mark every cell a
 * capsule fits in (`occludes` false), flood from the entrance, ask whether the fill
 * reaches the outside — escapes for NO body radius down to 0.02 m, in the canonical
 * set and in ruin's and vault's dressings alike. There is no hole to plug. (Hollow's
 * fill does escape, because a hollow's CA carve legitimately bulges past the nominal
 * envelope; its containment is the ENVELOPE RULE's, below.)
 *
 * What was actually happening is a CHARACTER-CONTROLLER bug, not a geometry one:
 * solid resolution is a capped push-out, so any frame that displaces the body further
 * than the cap can never be recovered and the body walks through — worst at corners,
 * where a diagonal splits the step over two axes. Fixed at the source with a
 * sub-stepped integrate (`SOLID_STEP_M` in client/src/character/character.ts).
 *
 * Posts were measured against that and moved nothing: 50/72 corner escapes with them,
 * 50/72 without, because thickness cannot outrun an uncapped step. They also cost:
 * a `wallT × 2` post protrudes `wallT/2` INTO the room, and only vault draws its
 * canonical runs — so in ruin and hollow every room corner would gain an invisible
 * solid nub, breaking this file's own law that a canonical wall is the SHORTEST the
 * theme can draw. If a future junction genuinely needs sealing, re-run the flood fill
 * FIRST and let it name the cell.
 */


// ── lids (enclosed themes) ────────────────────────────────────────────────────

/**
 * Half-extent cap on ONE canonical lid tile, metres.
 *
 * ⚠⚠ THIS IS A COLLISION-CORRECTNESS BOUND, NOT A PERF KNOB — the same 40 m bucket
 * invariant {@link cacheWallReach} guards, and the same lesson `hollow`'s
 * `MAX_RECT_HALF_M` was born from (an unbounded merge produced a 3.5 × 189 m strip
 * whose 95 m reach made the lid stop answering queries from most of its own room).
 * A wall run is thin so it clears the invariant by construction; a LID is wide on
 * BOTH horizontal axes, so it does not. Every lid is therefore TILED to this cap.
 * 18 m gives a worst-case reach of hypot(18, ceil/2, 18) ≈ 26 m — comfortably under
 * {@link COLLIDER_MAX_M}.
 */
export const CACHE_LID_HALF_MAX_M = 18;

/** Canonical lid slab thickness, metres (the themes draw 0.5–0.6). */
export const CACHE_CEIL_T = 0.6;

/**
 * Emit an axis-aligned lid over the rect [x0,x1] × [z0,z1], TILED so no tile breaks
 * the {@link CACHE_LID_HALF_MAX_M} reach cap. Tiles abut exactly (no seam a bolt can
 * thread) because each one's half-extent is derived from the same integer division.
 */
export function emitLid(
  out: CacheWallBox[],
  x0: number, x1: number, z0: number, z1: number,
  baseH: number, thickness = CACHE_CEIL_T,
): void {
  const lx = x1 - x0, lz = z1 - z0;
  if (!(lx > 0.05) || !(lz > 0.05)) return;
  const nx = Math.max(1, Math.ceil(lx / (2 * CACHE_LID_HALF_MAX_M)));
  const nz = Math.max(1, Math.ceil(lz / (2 * CACHE_LID_HALF_MAX_M)));
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < nz; j++) {
      const ax = x0 + (lx * i) / nx, bx = x0 + (lx * (i + 1)) / nx;
      const az = z0 + (lz * j) / nz, bz = z0 + (lz * (j + 1)) / nz;
      out.push({
        cx: (ax + bx) / 2, cz: (az + bz) / 2, along: "x",
        halfLen: (bx - ax) / 2, halfT: (bz - az) / 2,
        baseH, height: thickness,
      });
    }
  }
}

/**
 * Lids over every room and every corridor of an enclosed layout, all at ONE height.
 *
 * ⚠ ONE HEIGHT FOR THE WHOLE INSTANCE, deliberately — see {@link CacheWallParams.ceilH}.
 * Per-class lids (a low hall lid, a tall arena lid) leave a horizontal SLIT at each
 * corridor mouth: above the hall lid, below the room lid, laterally open because the
 * room's perimeter wall is cut for the doorway. One height means every wall top meets
 * every lid base and the enclosure has no seam at all.
 */
export function emitCeilings(
  layout: CacheLayoutV1,
  field: CacheWalkField,
  o: { wallT: number; corridorHalf: number; ceilH: number },
): CacheWallBox[] {
  const out: CacheWallBox[] = [];
  for (const r of layout.rooms) {
    emitLid(
      out,
      r.x - r.w / 2 - o.wallT, r.x + r.w / 2 + o.wallT,
      r.z - r.d / 2 - o.wallT, r.z + r.d / 2 + o.wallT,
      o.ceilH,
    );
  }
  const half = o.corridorHalf + o.wallT;
  for (const c of field.corrs) {
    for (let k = 0; k + 3 < c.pts.length; k += 2) {
      const ax = c.pts[k], az = c.pts[k + 1], bx = c.pts[k + 2], bz = c.pts[k + 3];
      const len = pmath.hypot(bx - ax, bz - az);
      if (len < 0.05) continue;
      // A corridor leg is axis-aligned by construction (cell-to-cell), so the lid over
      // it is an axis-aligned rect: long on the leg's axis, `half` wide across.
      const alongX = Math.abs(bx - ax) > Math.abs(bz - az);
      const x0 = alongX ? Math.min(ax, bx) - half : ax - half;
      const x1 = alongX ? Math.max(ax, bx) + half : ax + half;
      const z0 = alongX ? az - half : Math.min(az, bz) - half;
      const z1 = alongX ? az + half : Math.max(az, bz) + half;
      emitLid(out, x0, x1, z0, z1, o.ceilH);
    }
  }
  return out;
}

/** Midpoint of corridor `ci` plus the axis a barrier ACROSS it runs on. */
export function corridorMidpoint(c: CorrFeat): { cx: number; cz: number; along: "x" | "z" } {
  const target = c.len / 2;
  let seg = 0;
  while (seg < c.cum.length - 2 && c.cum[seg + 1] < target) seg++;
  const t = (target - c.cum[seg]) / Math.max(1e-6, c.cum[seg + 1] - c.cum[seg]);
  const ax = c.pts[2 * seg], az = c.pts[2 * seg + 1];
  const bx = c.pts[2 * seg + 2], bz = c.pts[2 * seg + 3];
  return {
    cx: ax + (bx - ax) * t,
    cz: az + (bz - az) * t,
    along: Math.abs(bx - ax) > Math.abs(bz - az) ? "z" : "x", // ACROSS travel
  };
}

/** One closed barrier per `gate` edge, seated at its corridor's midpoint. */
export function buildGates(
  layout: CacheLayoutV1,
  field: CacheWalkField,
  o: { halfLen: number; halfT: number; height: number; sink: number },
): CacheGateBox[] {
  const gates: CacheGateBox[] = [];
  layout.edges.forEach((e, ei) => {
    if (e.kind !== "gate") return;
    const m = corridorMidpoint(field.corrs[ei]);
    const h = field.corrFloorAt(ei, m.cx, m.cz);
    gates.push({
      edgeIndex: ei,
      open: false,
      box: {
        cx: m.cx, cz: m.cz, along: m.along,
        halfLen: o.halfLen, halfT: o.halfT,
        baseH: h - o.sink, height: o.height + o.sink,
      },
    });
  });
  return gates;
}

// ── collider fold ─────────────────────────────────────────────────────────────

/** Planet-local point at pad-local (x, z) and ABSOLUTE height `h` above the sphere. */
export function localPoint(R: number, x: number, z: number, h: number): [number, number, number] {
  const rr = R + h;
  return [x, Math.sqrt(Math.max(0, rr * rr - x * x - z * z)), z];
}

/** One {@link CacheWallBox} as the {@link BoxSpec} the collider field ingests. */
export function wallSpec(R: number, b: CacheWallBox, standable = false): BoxSpec {
  const base = localPoint(R, b.cx, b.cz, b.baseH);
  const l = pmath.hypot(base[0], base[1], base[2]) || 1;
  const up: [number, number, number] = [base[0] / l, base[1] / l, base[2] / l];
  const axis: [number, number, number] = b.along === "x" ? [1, 0, 0] : [0, 0, 1];
  const dot = axis[0] * up[0] + axis[1] * up[1] + axis[2] * up[2];
  const wx = axis[0] - up[0] * dot, wy = axis[1] - up[1] * dot, wz = axis[2] - up[2] * dot;
  const wl = pmath.hypot(wx, wy, wz) || 1;
  return {
    base,
    up,
    width: [wx / wl, wy / wl, wz / wl],
    halfWidth: b.halfLen,
    height: b.height,
    halfDepth: b.halfT,
    standable,
  };
}

/**
 * Fold every solid into a fresh {@link BoxColliderField}. Open gates are skipped —
 * this is the whole implementation of a gate toggle (the field has no removal seam,
 * so opening a gate rebuilds it).
 */
export function buildBoxField(
  R: number,
  boxGroups: readonly (readonly CacheWallBox[])[],
  gates: readonly CacheGateBox[],
): BoxColliderField {
  const f = new BoxColliderField(R);
  for (const group of boxGroups) for (const b of group) f.addBox(wallSpec(R, b));
  for (const g of gates) if (!g.open) f.addBox(wallSpec(R, g.box));
  return f;
}

// ── the canonical set ─────────────────────────────────────────────────────────

/**
 * Per-theme geometry constants — the numbers the client's three dressers already use.
 *
 * ⚠ WALL HEIGHTS ARE THE THEME'S *MINIMUM*, DELIBERATELY. `vault` jitters each room's
 * headroom above its minimum from the dresser's own rng stream, and `hollow` does the
 * same for its ceiling shell. Reproducing those draws here would couple the shared
 * canonical set to the order of every draw in a client file — the exact fragility
 * `roomFloorHeights`' stream-alignment comment warns about. So the canonical wall is
 * the SHORTEST the theme can draw: a bolt is never stopped by a wall the player cannot
 * see, only (very rarely, on a lobbed shot) allowed over one that is really taller.
 */
export interface CacheWallParams {
  wallT: number;
  /** Wall height over an ordinary room. */
  wallH: number;
  /** Wall height over an `arena`/`boss` room (vault's taller halls). */
  arenaWallH: number;
  /** Wall height along a corridor flank. */
  corridorH: number;
  corridorHalf: number;
  /** Base sink below the floor, before the live allowance. */
  sink: number;
  gateHalfLen: number;
  gateHalfT: number;
  gateH: number;
  /**
   * ENCLOSED THEMES ONLY — the canonical lid height above the floor datum, and with
   * it the height every wall, flank and gate is RAISED to. `null` = open sky (`ruin`).
   *
   * ⚠ THIS IS THE THEME'S *MAXIMUM*, THE EXACT OPPOSITE OF {@link CacheWallParams.wallH}
   * — and for the same reason. A canonical wall is the shortest the theme can draw so a
   * bolt is never stopped by masonry the player cannot see. A canonical CEILING is the
   * highest the theme can draw, so a bolt is never stopped in what looks like open air
   * under a visibly higher roof. Both dressers jitter their ceilings off their own rng
   * stream (vault: 3.6/4.8 + up to 0.7/0.8; hollow: 3.6/5.2 + up to 1.0/1.6 + 0.4 of
   * lid safety), and reproducing those draws here would couple the shared set to the
   * order of every draw in a client file. So we take the theme's ceiling CEILING:
   * anything the dresser can actually build is at or below it, and the only cost is
   * that a bolt fired straight up in a low room dies a few decimetres above a roof
   * slab it has already disappeared into.
   */
  ceilH: number | null;
}

/**
 * The three dressers' own constants, mirrored. Sources:
 * `client/src/cache/theme/{ruin,vault,hollow}.ts`.
 *
 * ⚠ `hollow` IS AN APPROXIMATION AND THE ONLY ONE. Its client dressing has no
 * perimeter or flank walls at all — the cave is carved by a cellular automaton and its
 * solids are merged rock PRISMS, which depend on the dresser's rng and its CA passes.
 * The canonical set therefore seals hollow along the NOMINAL room/corridor envelope
 * that carve is grown from. Consequence, accepted for now: in a hollow, a bolt fired
 * into a CA alcove that bulges past the nominal corridor stops at the nominal line
 * rather than at the visible rock. TODO: promote the carve itself (it is pure) and
 * this footnote goes away.
 */
export const CACHE_WALL_PARAMS: Record<CacheTheme, CacheWallParams> = {
  ruin: {
    // ⚠ `corridorHalf` WIDENED MORE THAN PROPORTIONALLY with the CELL_M scale-up
    // (space.ts 12/14 → 18/20, +50%): 3.0 → 4.5 here is +50% too, but the cell grew
    // under it, so the corridor is a wider FRACTION of the run it sits in — which is
    // what live play was actually asking for. The invariant it must not break is that a
    // mouth stays an OPENING rather than the whole wall: the narrowest room is a
    // `gate`/`shortcut` at ROLE_SCALE 0.60, i.e. 0.60 · 18 = 10.8 m wide at tier 0 →
    // 5.4 m half, so 4.5 leaves 0.9 m of jamb each side (it was 0.6 m before).
    // `gateHalfLen` is `corridorHalf + wallT` — the span that seals corridor AND flanks,
    // and the same expression the client's own gate box draws (theme/ruin.ts).
    wallT: 0.7, wallH: 4.6, arenaWallH: 4.6, corridorH: 4.6, corridorHalf: 4.5,
    sink: 0.4, gateHalfLen: 5.2, gateHalfT: 0.35, gateH: 4.2,
    // OPEN SKY BY DESIGN. A ruin is an outdoor site: lobbing a shot over a broken
    // wall is a legitimate play, and containment is the ENVELOPE RULE's job, not a
    // lid's. (`cacheEnvelope` gives ruin an infinite altitude bound to match.)
    ceilH: null,
  },
  vault: {
    // corridorHalf 2.5 → 4.0 (+60%) — a vault's masonry corridors read as the tightest
    // of the three, so they take the biggest share of the widening. gateHalfLen stays
    // `corridorHalf + wallT`.
    wallT: 0.4, wallH: 3.6, arenaWallH: 4.8, corridorH: 3.6, corridorHalf: 4.0,
    sink: 0.4, gateHalfLen: 4.4, gateHalfT: 0.25, gateH: 3.6,
    // client/src/cache/theme/vault.ts: VAULT_ARENA_HEADROOM_MIN 4.8 + ARENA_HEADROOM_VARY 0.8
    // is the tallest lid the dresser can draw (ordinary rooms top out at 3.6 + 0.7,
    // halls are a flat 3.6).
    ceilH: 5.6,
  },
  hollow: {
    // corridorHalf 2.4 → 4.0 (+67%). Hollow's gate span keeps its own historic
    // `corridorHalf + 1.0` margin (wallT is 0.8, and the CA carve bulges past the
    // nominal line — the extra 0.2 m is what stops a gate ending inside a rock alcove).
    wallT: 0.8, wallH: 3.6, arenaWallH: 5.2, corridorH: 3.6, corridorHalf: 4.0,
    sink: 0.4, gateHalfLen: 5.0, gateHalfT: 0.3, gateH: 4.0,
    // client/src/cache/theme/hollow.ts: HOLLOW_CHAMBER_HEADROOM 5.2 + CHAMBER_VARY 1.6
    // + CEIL_SAFETY 0.4 (tunnels top out at 3.6 + 1.0 + 0.4) + FLOOR_UNDULATE 0.8.
    // ⚠ THE UNDULATION TERM IS NOT COSMETIC. A hollow's lid is hung off the HIGHEST
    // floor its merged rect spans, and a hollow's floor undulates ±0.8 m even in flat
    // live mode — so 7.2 left 4 rooms in 19 with a visible ceiling ABOVE the canonical
    // one, i.e. a tracer dying in open air. Measured worst over 120 layouts: 7.886 m.
    ceilH: 8.0,
  },
};

export interface CacheWallOptions {
  /** Override the layout's own theme (tests/diagnostics). */
  theme?: CacheTheme;
  /**
   * LIVE mode — the default, because it is the only mode a cache is ever PLAYED in.
   * Floors are one datum (see `liveCache.ts`'s header) and every solid takes the
   * {@link LIVE_SINK_EXTRA_M} allowance. `false` reproduces the lab's terraced floors.
   */
  live?: boolean;
}

/**
 * THE canonical solids of a cache layout: room-boundary walls on every non-mouth side,
 * corridor flank walls, and one closed barrier per gate edge.
 *
 * Pure `f(layout)` — no rng, no clock, no theme rng stream — so the server's collider
 * field, the client's bolt field and any test all fold the SAME boxes. This is the
 * answer to "what stops a bullet in a cache"; the themes' extra dressing (a vault's
 * ceilings, a hollow's prisms) is decoration layered on top of it, client-side.
 */
export function cacheWallBoxes(layout: CacheLayoutV1, opts: CacheWallOptions = {}): CacheWallSet {
  const theme = opts.theme ?? layout.theme;
  const p = CACHE_WALL_PARAMS[theme] ?? CACHE_WALL_PARAMS.ruin;
  const live = opts.live !== false;
  const sink = p.sink + (live ? LIVE_SINK_EXTRA_M : 0);

  const floorH = new Float64Array(layout.rooms.length);
  if (!live) for (let i = 0; i < floorH.length; i++) floorH[i] = layout.rooms[i].floorH;

  const field = new CacheWalkField(layout, floorH, p.corridorHalf, p.wallT + 2);
  // Corridors may legally CROSS, so a run that would seal another route is cut where
  // its chunk centre stands on walkable ground (the −0.15 m slack matches the themes).
  const allow = (x: number, z: number): boolean => field.nearestSd(x, z) > -0.15;

  // ENCLOSED THEMES SEAL TO THE LID. Every solid rises to the one canonical ceiling
  // height so wall top == lid base everywhere — otherwise the band between a short
  // hall wall and a tall room lid is an open horizontal slit at every doorway.
  const ceil = p.ceilH;
  const walls: CacheWallBox[] = [];
  emitRoomPerimeter(walls, layout, field, floorH, {
    wallT: p.wallT,
    height: ceil ?? p.wallH,
    ...(ceil === null ? {
      heightOf: (i: number): number => {
        const role = layout.rooms[i].role;
        return role === "arena" || role === "boss" ? p.arenaWallH : p.wallH;
      },
    } : {}),
    sink,
    allow,
    corridorHalf: p.corridorHalf,
    chunk: 1.0,
  });
  emitCorridorFlanks(walls, field, {
    wallT: p.wallT,
    height: ceil ?? p.corridorH,
    sink,
    allow,
    corridorHalf: p.corridorHalf,
    chunk: 1.0,
  });
  const gates = buildGates(layout, field, {
    halfLen: p.gateHalfLen, halfT: p.gateHalfT, height: ceil ?? p.gateH, sink,
  });
  const ceilings = ceil === null
    ? []
    : emitCeilings(layout, field, { wallT: p.wallT, corridorHalf: p.corridorHalf, ceilH: ceil });
  return { walls, ceilings, gates };
}

/**
 * Largest bounding reach any box in a set will claim, metres.
 *
 * ⚠⚠ THE 40 m BUCKET INVARIANT. `BoxColliderField` sizes its bucket grid at
 * `2 · COLLIDER_MAX_M` and smears each feature over the buckets its reach covers; a
 * feature whose reach EXCEEDS {@link COLLIDER_MAX_M} can be missed by a query in a
 * neighbouring bucket — silently, which is the P7 lesson `MAX_RECT_HALF_M` was born
 * from. Every canonical box is a wall run (≤ a room's half-span + a metre) so this
 * holds by construction; the test asserts it, because "by construction" is exactly
 * what stops being true when a layout's room budget grows.
 */
export function cacheWallReach(set: CacheWallSet): number {
  let worst = 0;
  const of = (b: CacheWallBox): number => {
    const hh = b.height * 0.5;
    const r = pmath.hypot(b.halfLen, hh, b.halfT);
    if (r > worst) worst = r;
    return r;
  };
  for (const b of set.walls) of(b);
  for (const b of set.ceilings) of(b);
  for (const g of set.gates) of(g.box);
  return worst;
}

/**
 * The whole solid set as the groups {@link buildBoxField} ingests — walls THEN lids.
 * One helper so no consumer can fold a cache and quietly forget its ceilings, which
 * is exactly the class of omission that let flyers shoot down through a vault's roof.
 */
export function cacheBoxGroups(set: CacheWallSet): readonly (readonly CacheWallBox[])[] {
  return [set.walls, set.ceilings];
}

/** True when every box in the set respects the bucket invariant. */
export function cacheWallReachOk(set: CacheWallSet): boolean {
  return cacheWallReach(set) <= COLLIDER_MAX_M;
}

// ── the ENVELOPE rule ─────────────────────────────────────────────────────────
//
// ⚠⚠ CONTAINMENT IS A RULE, NOT A MESH. Live play (2026-07-28) reported players
// simply leaving a dungeon over its side walls, and geometry cannot fix that: the
// canonical solids are 3.6–5.6 m and the movement kit clears them trivially — a
// chargeable super-jump, an 11 m/s boost leap, a glide. Building taller walls is a
// treadmill against the character controller and loses every round.
//
// So the instance carries a BOUND instead: a pad-local box the server tests every
// tick, with a warn band before it and a relocation past it. It is cheap (two
// comparisons), it cannot be out-run by any future movement toy, and it is one
// number per layout rather than a per-theme geometry problem.

/**
 * How far past a layout's own half-extent a player may legitimately be, metres.
 *
 * A layout's `extent` is the half-extent of its ROOM GRID, and the themes dress a
 * little past it (a ruin's ridges, a vault's masonry fill, a hollow's rock band all
 * run a few metres beyond the outermost wall). 15 m clears that skirt twice over
 * while still being a fraction of the ~100–190 m layouts, so nobody is ejected from
 * a legal corner and nobody gets a usable field to play in outside the walls.
 */
export const CACHE_OOB_GRACE_M = 15;

/** Fraction of the grace band that reads as WARN rather than legal ground. */
export const CACHE_OOB_WARN_FRAC = 0.4;

/**
 * Headroom allowed above an enclosed theme's lid before the altitude bound bites.
 *
 * Non-zero on purpose: the lid is a real solid, so nobody reaches this by walking —
 * the bound exists for the case the LID ITSELF fails (a dressing bug, a gap at a
 * crossing, a future theme). 6 m is beyond any jump from the floor of a 5.6 m room
 * yet well under the height a glide would reach if the roof really were open.
 */
export const CACHE_CEIL_GRACE_M = 6;

/** The live bounds of one instance, in pad-local metres above the floor datum. */
export interface CacheEnvelope {
  /** Chebyshev half-extent past which a player is RELOCATED. */
  lateral: number;
  /** …and past which they are warned. */
  warnLateral: number;
  /** Height above the datum past which a player is relocated (Infinity = open sky). */
  ceiling: number;
  warnCeiling: number;
}

/**
 * The envelope of a layout: `f(extent, theme)`, pure, both sides can derive it.
 * Chebyshev (a box, not a disc) because `extent` is itself a half-extent of the
 * square room grid — a radial bound would eject a legal corner room.
 */
export function cacheEnvelope(layout: CacheLayoutV1, theme?: CacheTheme): CacheEnvelope {
  const p = CACHE_WALL_PARAMS[theme ?? layout.theme] ?? CACHE_WALL_PARAMS.ruin;
  const lateral = layout.extent + CACHE_OOB_GRACE_M;
  const ceiling = p.ceilH === null
    ? Infinity
    : p.ceilH + CACHE_CEIL_T + CACHE_CEIL_GRACE_M;
  return {
    lateral,
    warnLateral: lateral - CACHE_OOB_GRACE_M * CACHE_OOB_WARN_FRAC,
    ceiling,
    warnCeiling: ceiling === Infinity ? Infinity : ceiling - CACHE_CEIL_GRACE_M * CACHE_OOB_WARN_FRAC,
  };
}

/** Where a pad-local point stands against the envelope. */
export type CacheEnvelopeVerdict = "in" | "warn" | "out";

/** Test a pad-local (x, z) at height `h` above the datum against the envelope. */
export function cacheEnvelopeTest(env: CacheEnvelope, x: number, z: number, h: number): CacheEnvelopeVerdict {
  const lat = Math.abs(x) > Math.abs(z) ? Math.abs(x) : Math.abs(z);
  if (lat > env.lateral || h > env.ceiling) return "out";
  if (lat > env.warnLateral || h > env.warnCeiling) return "warn";
  return "in";
}

/**
 * Hover clearance a FLYING enemy is capped to inside an enclosed instance, metres —
 * `null` where the sky is open (`ruin`) and the archetype's own clearance stands.
 *
 * ⚠ WITHOUT THIS, CEILINGS MAKE VAULTS UNPLAYABLE. Every flying archetype hovers at
 * `MovementProfile.clearance` = 8 m (locomotion treats it as a FLOOR, not a target),
 * which in a 5.6 m vault puts the whole ranged garrison ABOVE the roof — which is
 * precisely why live play saw enemies shooting down through ceilings that were not
 * there. Give those ceilings substance and the same drones become permanently
 * occluded ornaments floating over the level. So an enclosed instance caps hover to
 * a body's height below its lid: the drone is in the room with you, and the lid
 * blocks only the shots that really are coming through a roof.
 */
export function cacheFlyerClearance(theme: CacheTheme): number | null {
  const h = (CACHE_WALL_PARAMS[theme] ?? CACHE_WALL_PARAMS.ruin).ceilH;
  return h === null ? null : Math.max(1.6, h - 1.8);
}
